import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, NgModuleRef, OnInit } from '@angular/core';
import { RbacServiceInterface } from '../../services/rbac/rbac-service.interface';
import { EventsServiceInterface } from '../../services/events/events.interface';
import { INotification } from '../notifications-menu/services/notifications/notifications-service.interface';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { IFilterValues } from '../../services/tables/interfaces/table.service.interface';
import { Observable, Subject, timer } from 'rxjs';
import { IMODALS_SERVICE, IModalsService } from '@platform500services/p500-ui-kit';
import { NotificationsModal } from '../../modals/notifications-modal/notifications.modal';
import { SavedTabs } from './tabs.interface';
import { localStorageTabsKey, tabs } from './notification.helper';
import { StorageMap } from '@ngx-pwa/local-storage';
import { NotificationsService } from './notifications-service/notifications.service';
import { RbacPermissionsConstant } from '../../services/rbac/rbac-permissions.constant';
import { NavigationEnd, Router } from '@angular/router';
import { PageServiceInterface } from '../../services/page/page-service.interface';
import { audit, filter, repeat, takeUntil } from 'rxjs/operators';
import { NotificationInterface } from '../notification-description/notification.interface';
import { ISettingsServiceInterface } from '../../services/settings/settings-service.interface';
import { bannersData, IBanner } from '../banner/banners';

@Component({
  selector: 'gtd-notifications-sidebar',
  templateUrl: './notifications-sidebar.component.html',
  providers: [NotificationsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@DestroySubscribers()
export class NotificationsSidebarComponent implements OnInit {
  @Input() cssClass = '';
  public notifications$: Observable<INotification[]>;
  public page = 1;
  public isSettingLoaded = false;
  public tabs = tabs;
  public limit: number;
  public RbacPermissionsConstant = RbacPermissionsConstant;
  private click$ = new Subject();
  private subscribers: any = {};
  public banner: IBanner;
  private readonly settingsPage = 'dashboard';
  public limitOptions = [5, 10, 25, 50, 100];

  constructor(
    @Inject('RbacServiceInterface') private rbacService: RbacServiceInterface,
    @Inject('EventsServiceInterface') private eventsService: EventsServiceInterface,
    @Inject(IMODALS_SERVICE) private modalsService: IModalsService,
    @Inject(StorageMap) private localStorage: StorageMap,
    private notificationsService: NotificationsService,
    private cd: ChangeDetectorRef,
    private router: Router,
    @Inject('PageServiceInterface') private pageServiceInterface: PageServiceInterface,
    private moduleRef: NgModuleRef<any>,
    @Inject('ISettingsService') private settingsService: ISettingsServiceInterface
  ) {
    this.limit = this.notificationsService.limit;
    this.subscribers.router = this.router.events
      .pipe(
        filter((routerEvent) => routerEvent instanceof NavigationEnd),
        audit(() => this.click$),
        takeUntil(timer(0)),
        repeat()
      )
      .subscribe(() => {
        this.pageServiceInterface.refresh();
      });
  }

  handleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.click$.next(void 0);
  }

  close() {
    this.eventsService.broadcast('isOpenNotificationsSidebar:toggle');
  }

  onPageChange(page: number): void {
    this.page = page;
    this.getNotificationsData(page);
  }

  onLimitChange(limit: number): void {
    this.limit = this.notificationsService.limit = limit;
    this.getNotificationsData();
  }

  setFilterById(id: number[]): void {
    let filters: IFilterValues = { id };

    if (id.length === 0) {
      filters = {};
    }

    this.getNotificationsData(1, filters);
  }

  getNotificationsData(page = 1, filters: IFilterValues = {}): void {
    this.notifications$ = this.notificationsService.getNotificationsData(this.activeTab, page, filters);
    this.localStorage.set(localStorageTabsKey, this.tabs).subscribe();
  }

  get mainTabs(): string[] {
    return Object.entries(this.tabs.groups).reduce((acc, [key, value]) => {
      if (value.subTabs?.length) {
        acc.push(key);
      }
      return acc;
    }, []);
  }

  get subTabs(): string[] {
    if (!this.tabs.groups[this.tabs.activeTabTitle]) {
      this.tabs.activeTabTitle = Object.keys(this.tabs.groups)[0];
    }
    return this.tabs.groups[this.tabs.activeTabTitle].subTabs
      .map((key) => Object.keys(key)[0])
      .map((title, index, array) => {
        /**
         * the selected item from the drop-down list is always on the 4th position
         */
        if (index === 3) {
          const selectedIndex = array.lastIndexOf(this.activeSubTitle);
          if (selectedIndex >= 3) {
            array[selectedIndex] = title;
            return this.activeSubTitle;
          }
        }
        return title;
      });
  }

  get activeSubTitle(): string {
    return this.tabs.activeSubTabTitle[this.tabs.activeTabTitle] || this.subTabs[0];
  }

  get isLoading(): boolean {
    return this.notificationsService.isLoading;
  }

  get totalItems(): number {
    return this.notificationsService.totalItems;
  }

  private get activeTab(): { [key: string]: string } {
    return { [this.tabs.activeTabTitle]: this.activeSubTitle };
  }

  openNotificationsModal(notification: NotificationInterface): void {
    this.modalsService.open(NotificationsModal, { notification }, this.moduleRef);
  }

  ngOnInit(): void {
    this.localStorage.get(localStorageTabsKey).subscribe((tableTabs: SavedTabs) => {
      /**
       * check permissions in tabs
       */
      let isTabDeleted = false;
      for (const key in this.tabs.groups) {
        if (key in this.tabs.groups) {
          const subTabs = this.tabs.groups[key].subTabs.filter((child) => {
            const rbac = this.rbacService.can(Object.values(child)[0]);
            if (rbac === false) isTabDeleted = true;
            return rbac;
          });
          if (subTabs.length) {
            this.tabs.groups[key].subTabs = subTabs;
          } else {
            delete this.tabs.groups[key];
          }
        }
      }
      if (!isTabDeleted && tableTabs?.activeTabTitle && tableTabs?.activeSubTabTitle) {
        this.tabs.activeTabTitle = tableTabs.activeTabTitle;
        this.tabs.activeSubTabTitle = tableTabs.activeSubTabTitle;
      } else {
        this.tabs.activeTabTitle = Object.keys(this.tabs.groups)[0];
        this.tabs.activeSubTabTitle = Object.entries(this.tabs.groups).reduce((acc, [key, { subTabs }]) => {
          acc[key] = subTabs?.length ? Object.keys(subTabs[0])[0] : {};
          return acc;
        }, {});
      }
      this.getNotificationsData();
      this.isSettingLoaded = true;
      this.cd.detectChanges();
    });

    this.settingsService.loadSettings().subscribe((settings) => {
      bannersData
        .filter((banner) => banner.enabledInNotifications)
        .forEach((banner) => {
          if (!settings.dashboard['banner-notifications-closed_' + banner.id]) {
            this.banner = banner;
          }
        });
    });
  }

  public bannerClose(): void {
    this.settingsService.saveSettingsByKey(this.settingsPage, 'banner-notifications-closed_' + this.banner.id, true);
    this.banner = null;
  }
}
